import { useRef, useCallback, useReducer } from 'react';
import { useIsMounted } from './useIsMounted';
import { ApiError } from './useApiErrorMsgParser';

export enum AsyncStatus {
  Idle = 'idle',
  Pending = 'pending',
  Success = 'success',
  Failure = 'failure',
}

export type AsyncFunction<T> = (...args: any[]) => Promise<T>;

export interface AsyncStateData<TResult, TError = ApiError> {
  status: AsyncStatus;
  value?: TResult;
  error?: TError;
}

export interface AsyncState<T, TExecFunc extends AsyncFunction<T>, TError = ApiError>
  extends AsyncStateData<T, TError> {
  execute: (...args: Parameters<TExecFunc>) => Promise<void>;
  reset: () => void;
}

export interface AsyncStateDispatchAction<TResult, TError> {
  type: 'start' | 'complete' | 'fail' | 'reset';
  value?: TResult;
  error?: TError;
}

export function asyncStateDispatcher<TResult, TError = ApiError>(
  state: AsyncStateData<TResult, TError>,
  action: AsyncStateDispatchAction<TResult, TError>
): AsyncStateData<TResult, TError> {
  const pendingState = {
    status: AsyncStatus.Pending,
  };

  switch (action.type) {
    case 'start':
      return state.status === AsyncStatus.Success ? state : pendingState;
    case 'reset':
      return {
        status: AsyncStatus.Idle,
      };
    case 'complete':
      return {
        status: AsyncStatus.Success,
        value: action.value,
      };
    case 'fail':
      return {
        status: AsyncStatus.Failure,
        error: action.error,
      };
    default:
      // eslint-disable-next-line no-console
      console.warn(`Attempted unknown action on useAsync state [${action.type as string}]`);
      return state;
  }
}
interface UseAsyncOptions {
  autoReset: boolean;
}

export const useAsync = <T, TError = ApiError>(
  fn: AsyncFunction<T>,
  opts?: UseAsyncOptions
): AsyncState<T, AsyncFunction<T>, TError> => {
  const autoReset = opts?.autoReset || false;

  const [stateData, dispatch] = useReducer(asyncStateDispatcher, {
    status: AsyncStatus.Idle,
  });
  const { value, error, status } = stateData as AsyncStateData<T, TError>;
  const isMounted = useIsMounted();

  // Store the status as a reference so we can use within the callback without creating new versions
  // of the execute function when the status changes.
  const statusRef = useRef(status);
  statusRef.current = status;

  const execute = useCallback(
    async (...args: Parameters<AsyncFunction<T>>) => {
      if (statusRef.current === AsyncStatus.Pending) {
        return;
      }
      if (autoReset) {
        dispatch({ type: 'reset' });
      }

      dispatch({
        type: 'start',
      });
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        const result = (await fn(...args)) as T;
        if (isMounted()) {
          dispatch({ type: 'complete', value: result });
        }
      } catch (err) {
        if (isMounted()) {
          dispatch({ type: 'fail', error: err as TError });
        }
      }
    },
    [fn, dispatch, isMounted, autoReset]
  );

  const reset = useCallback(() => {
    dispatch({ type: 'reset' });
  }, [dispatch]);

  return { value, error, status, execute, reset };
};
